import React, {FC, useEffect, useState} from 'react';
import LayoutDrawer from '../../../../core/components/layoutDrawer.component';
import CloseIcon from '@mui/icons-material/CloseRounded';
import {SwipeableDrawer} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {ColumnComponent} from '../../../../core/components/base/column.component';
import Slider from 'react-slick';
import {RowComponent} from '../../../../core/components/base/row.component';
import momentJalali from 'moment-jalaali';
import momentEnglish from 'moment';
import momentHijri from 'moment-hijri';
import {SWIPER_BREAKPOINTS} from '../CONSTANTS';
import {toEnglishNumber, toPersianNumber, toPersianNumberFromNumber} from '../../../../core/utils/number.utils';

interface CalendarDrawerProps {
  type: number;
  calendarChoose: any;
  calendarOpen: boolean;
  calendarOpenFunction: any;
  local: string;
  initialDate: string;
  title: string;
}

export const CalendarDrawer: FC<CalendarDrawerProps> = (props) => {
  const { t } = useTranslation();
  const [dayStartIndex, setDayStartIndex] = useState(0);
  const [monthStartIndex, setMonthStartIndex] = useState(0);
  const [yearStartIndex, setYearStartIndex] = useState(0);
  const [dayList, setDayList] = useState<string[]>([]);
  const [monthList, setMonthList] = useState<string[]>([]);
  const [monthNumList, setMonthNumList] = useState<string[]>([]);
  const [yearList, setYearList] = useState<string[]>([]);
  let selectedDate = ['1401', '0', '0'];
  let selectedDay = 0;
  let selectedMonth = 0;
  let selectedYear = 1401;

  function getMonthName(i: number): string {
    selectedDay = Number(selectedDate[2]) - 1;
    selectedMonth = Number(selectedDate[1]) - 1;
    switch (props.type) {
      case 0:
        selectedYear = Number(momentJalali().jYear() - Number(selectedDate[0]) + 100);
        return momentJalali().jMonth(i).format('jMMMM') + ` / ${toPersianNumberFromNumber(i + 1)}`;
      case 1:
        selectedYear = Number(momentHijri().iYear() - Number(selectedDate[0]) + 100);
        return momentHijri().iMonth(i).format('iMMMM') + ` / ${toPersianNumberFromNumber(i + 1)}`;
      case 2:
        selectedYear = Number(momentEnglish().year() - Number(selectedDate[0]) + 100);
        return momentEnglish().month(i).format('MMMM') + ` / ${toPersianNumberFromNumber(i + 1)}`;
      default:
        selectedYear = Number(momentJalali().jYear() - Number(selectedDate[0]) + 100);
        return momentJalali().jMonth(i).format('jMMMM') + ` / ${toPersianNumberFromNumber(i + 1)}`;
    }
  }

  function getYearName(i: number): string {
    switch (props.type) {
      case 0:
        return toPersianNumber((momentJalali().jYear() - i + 100).toString());
      case 1:
        return toPersianNumber((momentHijri().iYear() - i + 100).toString());
      case 2:
        return toPersianNumber((momentEnglish().year() - i + 100).toString());
      default:
        return toPersianNumber((momentJalali().jYear() - i + 100).toString());
    }
  }

  useEffect(() => {
    selectedDate = props.initialDate.split('/');
    let months = Array.apply(0, Array(12)).map(function (_, i) {
      return getMonthName(i);
    });
    setDayList([...Array(31)].map((item, index) => toPersianNumber((index + 1).toString())));
    setMonthList(months);
    setMonthNumList([...Array(12)].map((_, index) => index.toString()));
    setYearList(
      Array.apply(0, Array(200)).map(function (_, i) {
        return getYearName(i);
      })
    );
    setDayStartIndex(selectedDay);
    setMonthStartIndex(selectedMonth);
    setYearStartIndex(selectedYear);
    dayRef?.current?.slickGoTo(selectedDay, true);
    monthRef?.current?.slickGoTo(selectedMonth, true);
    yearRef?.current?.slickGoTo(selectedYear, true);
  }, [props.calendarOpen]);

  const dayRef = React.useRef<Slider>(null);
  const monthRef = React.useRef<Slider>(null);
  const yearRef = React.useRef<Slider>(null);

  const daySetting = {
    infinite: true,
    loop: true,
    ...SWIPER_BREAKPOINTS,
    afterChange: (e: number) => {
      setDayStartIndex(e);
    }
  };

  const monthSetting = {
    infinite: true,
    loop: true,
    ...SWIPER_BREAKPOINTS,
    afterChange: (e: number) => {
      setMonthStartIndex(e);
    }
  };

  const yearSetting = {
    infinite: false,
    loop: false,
    ...SWIPER_BREAKPOINTS,
    afterChange: (e: number) => {
      setYearStartIndex(e);
    }
  };

  function okClick() {
    props.calendarChoose(
      toEnglishNumber(yearList[yearStartIndex]),
      monthNumList[monthStartIndex],
      toEnglishNumber(dayList[dayStartIndex])
    );
  }

  return (
    <SwipeableDrawer
      anchor={'bottom'}
      open={props.calendarOpen}
      disableSwipeToOpen={true}
      PaperProps={{ square: false, elevation: 0, style: { backgroundColor: 'transparent' } }}
      onOpen={() => props.calendarOpenFunction(true)}
      onClose={() => props.calendarOpenFunction(!props.calendarOpen)}
    >
      <LayoutDrawer>
        <ColumnComponent className="totalCenter p-3">
          <CloseIcon fontSize={'large'} onClick={() => props.calendarOpenFunction(false)} className="closeIcon" />
          <div className="text-base w-full text-center p-2 text-bold mt-2">{props.title}</div>
          <ColumnComponent className={'totalCenter relative'}>
            <div className="w-full swiperCenterFrame absolute top-4 flex flex-col">
              <div className="grow" style={{ backgroundColor: 'var(--theme-page-line-color)' }} />
            </div>
            <RowComponent className="justify-center mt-4">
              <Slider ref={dayRef} className="w-1/3 totalCenter overflow-hidden" {...daySetting}>
                {dayList.map((item, index) => (
                  <div
                    className={`w-full text-center swiperStyle text-base ${
                      index === dayStartIndex ? 'mTextBlue text-bold' : 'mText'
                    }`}
                    key={index}
                    onClick={() => dayRef?.current?.slickGoTo(index, false)}
                  >
                    {item}
                  </div>
                ))}
              </Slider>
              <Slider ref={monthRef} className="w-1/3 totalCenter overflow-hidden" {...monthSetting}>
                {monthList.map((item, index) => (
                  <div
                    className={`w-full text-center swiperStyle text-base ${
                      index === monthStartIndex ? 'mTextBlue text-bold' : 'mText'
                    }`}
                    key={index}
                    onClick={() => monthRef?.current?.slickGoTo(index, false)}
                  >
                    {item}
                  </div>
                ))}
              </Slider>
              <Slider ref={yearRef} className="w-1/3 totalCenter overflow-hidden" {...yearSetting}>
                {yearList.map((item, index) => (
                  <div
                    className={`w-full text-center swiperStyle text-base ${
                      index === yearStartIndex ? 'mTextBlue text-bold' : 'mText'
                    }`}
                    key={index}
                    onClick={() => yearRef?.current?.slickGoTo(index, false)}
                  >
                    {item}
                  </div>
                ))}
              </Slider>
            </RowComponent>
            <div
              className="mt-3 calculateButton text-bold text-base text-center container-md mb-2"
              onClick={() => okClick()}
            >
              {t('predict.ok')}
            </div>
          </ColumnComponent>
        </ColumnComponent>
      </LayoutDrawer>
    </SwipeableDrawer>
  );
};
